/**
 * Yönetici için Yüz Kaydı Dialog'u — EmployeeProfile > PDKS sekmesinden açılır.
 *
 * Akış:
 *  1. Kamera aç.
 *  2. Yüz algıla — kalite kontrolü (tek yüz, boyut, kadraj).
 *  3. Yönetici "Örnek Al" düğmesine basar (veya oto: 5 örnek 700 ms arayla).
 *  4. 5 örnek toplandığında sunucuya POST /pdks/face/enroll/{employee_id}
 *  5. Descriptor sunucuya (128 float x5), ham foto sunucuya asla gitmez.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Camera, Save, Loader2, RefreshCcw, KeyRound, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  ensureFaceModels, detectSingleFaceWithDescriptor, faceQualityIssues,
  resetFaceModels,
} from "@/lib/faceRecognition";

const REQUIRED_SAMPLES = 5;

export function FaceEnrollDialog({ open, onClose, employeeId, employeeName, onDone }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const busyRef = useRef(false);
  const [modelReady, setModelReady] = useState(false);
  const [modelError, setModelError] = useState("");
  const [modelSlow, setModelSlow] = useState(false);
  const [samples, setSamples] = useState([]);        // [{descriptor, at}]
  const [issue, setIssue] = useState("");
  const [autoCapture, setAutoCapture] = useState(true);
  const [uploading, setUploading] = useState(false);

  const loadModels = useCallback(() => {
    setModelError(""); setModelSlow(false); setModelReady(false);
    const slowTimer = setTimeout(() => setModelSlow(true), 20000);
    ensureFaceModels()
      .then(() => { clearTimeout(slowTimer); setModelReady(true); })
      .catch((err) => {
        clearTimeout(slowTimer);
        setModelError(String(err?.message || err) || "Modeller yüklenemedi");
      });
  }, []);

  useEffect(() => {
    if (!open) return;
    loadModels();
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } }, audio: false,
        });
        streamRef.current = s;
        if (videoRef.current) { videoRef.current.srcObject = s; await videoRef.current.play(); }
      } catch (e) {
        toast.error("Kamera açılamadı — tarayıcı izinlerini kontrol edin");
      }
    })();
    return () => {
      try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
      streamRef.current = null;
      setSamples([]); setIssue(""); setUploading(false); setModelReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Otomatik yakalama — kaliteli yüzü ~700ms'te bir bir örnek olarak al
  useEffect(() => {
    if (!open || !modelReady) return;
    let cancelled = false;
    async function loop() {
      if (cancelled) return;
      const v = videoRef.current;
      if (!v || v.paused || v.readyState < 2 || busyRef.current) { setTimeout(loop, 700); return; }
      try {
        const res = await detectSingleFaceWithDescriptor(v);
        const issues = faceQualityIssues(res, v);
        if (issues.length > 0) {
          setIssue(issues[0]);
        } else if (res) {
          setIssue("");
          if (autoCapture && samples.length < REQUIRED_SAMPLES) {
            addSample(res.descriptor);
          }
        }
      } catch { /* ignore */ }
      setTimeout(loop, 700);
    }
    loop();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, modelReady, samples.length, autoCapture]);

  const addSample = (descriptor) => {
    setSamples((s) => (s.length < REQUIRED_SAMPLES ? [...s, { descriptor, at: Date.now() }] : s));
  };

  const captureNow = async () => {
    if (!videoRef.current) return;
    busyRef.current = true;
    try {
      const res = await detectSingleFaceWithDescriptor(videoRef.current);
      const issues = faceQualityIssues(res, videoRef.current);
      if (issues.length > 0) { toast.error(issues[0]); return; }
      addSample(res.descriptor);
    } finally { busyRef.current = false; }
  };

  const upload = async () => {
    if (samples.length < REQUIRED_SAMPLES) { toast.error(`${REQUIRED_SAMPLES} örnek gerekli`); return; }
    setUploading(true);
    try {
      await api.post(`/pdks/face/enroll/${employeeId}`, {
        descriptors: samples.map((s) => s.descriptor),
      });
      toast.success("Yüz kaydı başarılı");
      onDone && onDone();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setUploading(false); }
  };

  const reset = () => setSamples([]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="face-enroll-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Camera className="w-4 h-4 text-blue-400" /> Yüz Kaydı — {employeeName}</DialogTitle>
          <DialogDescription>
            Personelin farklı küçük baş açılarıyla {REQUIRED_SAMPLES} adet net yüz örneği alınacak. Ham görüntü sunucuya
            gönderilmez — sadece 128 boyutlu descriptor vektörleri kaydedilir.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div className="relative aspect-[4/3] bg-slate-950 rounded-sm overflow-hidden ring-1 ring-slate-800">
            <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover mirror" data-testid="face-enroll-video" />
            <div className="absolute inset-6 border-2 border-dashed border-blue-500/40 rounded-md pointer-events-none" />
            <style>{`.mirror{transform: scaleX(-1)}`}</style>
          </div>
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-widest font-mono text-slate-400">Durum</div>
            {modelError ? (
              <div className="space-y-2">
                <div className="text-rose-400 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" /> Modeller yüklenemedi</div>
                <div className="text-slate-400 text-[11px] break-words">{modelError}</div>
                <Button onClick={() => { resetFaceModels(); loadModels(); }} size="sm" className="rounded-sm bg-blue-600 hover:bg-blue-500">
                  <RefreshCcw className="w-3.5 h-3.5 mr-2" /> Yeniden Dene
                </Button>
              </div>
            ) : !modelReady ? (
              <div className="text-slate-300 text-sm space-y-1">
                <div><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Modeller yükleniyor...</div>
                {modelSlow && (
                  <div className="text-amber-400 text-[11px]">Bağlantınız yavaş görünüyor (7 MB). Lütfen bekleyin veya <button className="underline" onClick={() => { resetFaceModels(); loadModels(); }}>yeniden deneyin</button>.</div>
                )}
              </div>
            ) : issue ? (
              <div className="text-amber-400 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {issue}</div>
            ) : samples.length >= REQUIRED_SAMPLES ? (
              <div className="text-emerald-400 text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Yüz örnekleri hazır — Kaydet'e basın</div>
            ) : (
              <div className="text-slate-300 text-sm">Yüz algılanıyor... {samples.length}/{REQUIRED_SAMPLES} örnek alındı</div>
            )}
            <div className="flex gap-1">
              {Array.from({ length: REQUIRED_SAMPLES }).map((_, i) => (
                <div key={i} className={`h-2 flex-1 rounded-sm ${i < samples.length ? "bg-emerald-500" : "bg-slate-800"}`} />
              ))}
            </div>
            <div className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={autoCapture} onChange={(e) => setAutoCapture(e.target.checked)}
                id="auto" data-testid="face-enroll-auto" />
              <label htmlFor="auto" className="text-slate-400">Otomatik yakala</label>
            </div>
            {!autoCapture && (
              <Button onClick={captureNow} disabled={!modelReady || samples.length >= REQUIRED_SAMPLES}
                className="w-full rounded-sm bg-blue-600 hover:bg-blue-500" data-testid="face-enroll-capture">
                <Camera className="w-4 h-4 mr-2" /> Örnek Al ({samples.length}/{REQUIRED_SAMPLES})
              </Button>
            )}
            {samples.length > 0 && (
              <Button onClick={reset} variant="outline" size="sm" className="w-full rounded-sm">
                <RefreshCcw className="w-4 h-4 mr-2" /> Örnekleri Sıfırla
              </Button>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">İptal</Button>
          <Button onClick={upload} disabled={samples.length < REQUIRED_SAMPLES || uploading}
            className="bg-emerald-600 hover:bg-emerald-500 rounded-sm" data-testid="face-enroll-save">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Kaydı Yükle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/** Yönetici için basit PIN dialog'u — hashlenmiş şekilde saklanır. */
export function PinSetDialog({ open, onClose, employeeId, employeeName, onDone }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setPin(""); }, [open]);

  const save = async () => {
    if (!/^\d{4,8}$/.test(pin)) { toast.error("PIN 4-8 haneli rakam olmalı"); return; }
    setBusy(true);
    try {
      await api.post(`/pdks/pin/${employeeId}`, { pin });
      toast.success("PIN kaydedildi");
      onDone && onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="pin-set-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="w-4 h-4 text-blue-400" /> PDKS PIN — {employeeName}</DialogTitle>
          <DialogDescription>Yüz tanıma çalışmadığında personelin terminale PIN ile giriş yapabilmesi için 4-8 haneli sayısal PIN atayın. PIN veri tabanında hashlenmiş olarak saklanır.</DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-400 font-mono">Yeni PIN</Label>
          <Input type="password" inputMode="numeric" value={pin} maxLength={8}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            className="bg-slate-950 border-slate-800 rounded-sm mt-1 font-mono text-2xl text-center tracking-widest"
            data-testid="pin-set-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">İptal</Button>
          <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 rounded-sm" data-testid="pin-set-save">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
